/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';
import { Form, Col } from 'react-bootstrap';
import { useController, useFormContext } from 'react-hook-form';
import apiClient from 'api/client';
import { BsInfoCircleFill } from 'react-icons/bs';

/**
 * DynamicDropdown component for handling both standard and dependent dropdowns
 * with support for edit mode without label fields and cross-component dependencies
 *
 * @param {Object} props Component properties
 * @param {Object} props.metadata Field metadata including options, dependencies, etc.
 * @param {boolean} props.isDisabled Whether the dropdown is disabled
 * @param {Function} props.setDropdownOptions Callback to update parent options
 * @param {Array} props.externalOptions Options provided from parent (for cross-component deps)
 */

const DynamicDropdown = ({
  metadata,
  isDisabled = false,
  setDropdownOptions,
  externalOptions = null, // NEW: For cross-component dependencies
  excludeValues = [],
  allFields = {} // NEW: Pass all fields for dependency management
}) => {
  const [options, setOptions] = useState(metadata.options || []);
  const [isLoading, setIsLoading] = useState(false);
  const previousDependentValue = useRef(null);
  const optionsLoaded = useRef(false);
  const tns_tenant = '03534add672340809e2c70314b1ce20e';

  // Get form context
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues
  } = useFormContext();

  // Get the field value using useController
  const { field } = useController({
    name: metadata.id,
    control,
    defaultValue: metadata.defaultValue || (metadata.multiSelect ? [] : '')
  });

  // Watch the dependent field's value if this is a dependent dropdown
  const dependentValue = metadata.dependsOn ? watch(metadata.dependsOn) : null;

  // NEW: Handle external options (from cross-component dependencies)
  useEffect(() => {
    if (externalOptions && Array.isArray(externalOptions)) {
      console.log(
        `🔧 Modal dropdown ${metadata.id} received external options:`,
        externalOptions
      );
      setOptions(externalOptions);
      optionsLoaded.current = true;

      // Reset field value if current value is not in new options
      const currentValue = field.value;
      if (currentValue && !metadata.multiSelect) {
        const isValueInOptions = externalOptions.some(
          opt => opt.value === currentValue
        );
        if (!isValueInOptions) {
          console.log(`🔄 Resetting ${metadata.id} value - not in new options`);
          setValue(metadata.id, '');
          setValue(`${metadata.id}_label`, '');
        }
      }
    }
  }, [
    externalOptions,
    metadata.id,
    field.value,
    metadata.multiSelect,
    setValue
  ]);

  // Initialize component - resolve labels for initial values
  useEffect(() => {
    // Skip if external options will handle this
    if (externalOptions) return;

    // Handle case where we have a value but no label
    if (!optionsLoaded.current && field.value) {
      // For multi-select fields
      if (
        metadata.multiSelect &&
        Array.isArray(field.value) &&
        field.value.length > 0
      ) {
        // Check if each value has a corresponding option with a label
        const valuesWithoutLabels = field.value.filter(
          value => !options.some(option => option.value === value)
        );

        // If we have values without labels, try to add them from the main options
        if (valuesWithoutLabels.length > 0 && metadata.options) {
          const updatedOptions = [...options];

          // For each value without a label, try to find it in metadata.options
          valuesWithoutLabels.forEach(value => {
            const existingOption = metadata.options.find(
              opt => opt.value === value
            );
            if (existingOption) {
              // Add option if it doesn't exist in current options
              if (!updatedOptions.some(opt => opt.value === value)) {
                updatedOptions.push(existingOption);
              }

              // Create label field for the value
              setValue(`${metadata.id}_label_${value}`, existingOption.label);
            } else {
              // If no option exists, just use the value as the label
              if (!updatedOptions.some(opt => opt.value === value)) {
                updatedOptions.push({ value, label: value.toString() });
              }

              // Create label field
              setValue(`${metadata.id}_label_${value}`, value.toString());
            }
          });

          setOptions(updatedOptions);
        }
      }
      // For single select fields
      else if (!metadata.multiSelect && field.value) {
        const valueExists = options.some(
          option => option.value === field.value
        );

        if (!valueExists && metadata.options) {
          // Try to find the option in metadata.options
          const existingOption = metadata.options.find(
            opt => opt.value === field.value
          );

          if (existingOption) {
            setOptions(prev => [...prev, existingOption]);
            setValue(`${metadata.id}_label`, existingOption.label);
          } else {
            // If no option exists, use the value as the label
            setOptions(prev => [
              ...prev,
              { value: field.value, label: field.value.toString() }
            ]);
            setValue(`${metadata.id}_label`, field.value.toString());
          }
        }
      }

      optionsLoaded.current = true;
    }
  }, [
    field.value,
    options,
    metadata.id,
    metadata.multiSelect,
    metadata.options,
    setValue,
    externalOptions
  ]);

  // Load options from API
  useEffect(() => {
    // Skip if external options are being used
    if (externalOptions) return;

    // Skip if no endpoint is configured
    if (!metadata.endpoint) return;

    let dependentValue = null;

    if (metadata.dependsOn) {
      // Get dependent value
      dependentValue = watch(metadata.dependsOn);

      // Skip if no dependent value
      if (!dependentValue) return;

      // Skip if the dependent value hasn't changed (deep comparison for arrays)
      const isSame = Array.isArray(dependentValue)
        ? JSON.stringify(previousDependentValue.current) ===
          JSON.stringify(dependentValue)
        : previousDependentValue.current === dependentValue;

      if (isSame && optionsLoaded.current) return;

      console.log(
        `🎯 Modal Dropdown: Dependency triggered for ${metadata.id}, dependent value:`,
        dependentValue
      );

      // Update reference for next comparison
      previousDependentValue.current = dependentValue;
    } else {
      // For independent dropdowns, fetch once
      if (optionsLoaded.current) return;
      console.log(
        `🚀 Modal Dropdown: Initial API fetch for independent field ${metadata.id}`
      );
    }

    const fetchOptions = async () => {
      setIsLoading(true);

      try {
        let endpoint = metadata.endpoint;
        const dependencies = metadata.dependsOn
          ? Array.isArray(metadata.dependsOn)
            ? metadata.dependsOn
            : [metadata.dependsOn]
          : [];

        // Construct query parameters
        const urlParams = new URLSearchParams();
        let allSatisfied = true;

        if (dependencies.length > 0) {
          dependencies.forEach((depId, index) => {
            const val = Array.isArray(dependentValue)
              ? dependentValue[index]
              : dependentValue;

            if (!val || val === 'null') {
              allSatisfied = false;
            } else {
              urlParams.append(depId, val);
              // Also support placeholder replacement for any dependency field
              if (endpoint.includes(`{${depId}}`)) {
                endpoint = endpoint.replace(`{${depId}}`, val);
              }
            }
          });

          // Traditional {id} replacement (uses first dependency)
          const primaryValue = Array.isArray(dependentValue)
            ? dependentValue[0]
            : dependentValue;
          if (endpoint.includes('{id}') && primaryValue) {
            endpoint = endpoint.replace('{id}', primaryValue);
          }
        }

        if (metadata.dependsOn && !allSatisfied) {
          setIsLoading(false);
          return;
        }

        // Append query parameters if not already in URL (and not replaced by placeholders)
        const queryString = urlParams.toString();
        if (queryString) {
          // Check which params are already replaced or in the endpoint
          urlParams.forEach((value, key) => {
            if (
              !endpoint.includes(`{${key}}`) &&
              !endpoint.includes(`${key}=`)
            ) {
              endpoint +=
                (endpoint.includes('?') ? '&' : '?') + `${key}=${value}`;
            }
          });
        }

        console.log(`📡 Modal Dropdown API call: ${endpoint}`);

        const response = await apiClient.get(endpoint);

        if (response.data.results) {
          const newOptions = response.data.results;
          console.log(
            `📦 Modal Dropdown: Received ${newOptions.length} options for ${metadata.id}`
          );

          const currentValue = field.value;

          if (metadata.multiSelect) {
            // Multi-Select: Retain values not in the new options
            if (Array.isArray(currentValue) && currentValue.length > 0) {
              const valuesNotInNewOptions = currentValue.filter(
                value => !newOptions.some(opt => opt.value === value)
              );

              if (valuesNotInNewOptions.length > 0) {
                const optionsToAdd = valuesNotInNewOptions.map(value => {
                  const existingOption = options.find(
                    opt => opt.value === value
                  );
                  return existingOption || { value, label: value.toString() };
                });

                setOptions([...newOptions, ...optionsToAdd]);
              } else {
                setOptions(newOptions);
              }
            } else {
              setOptions(newOptions);
            }
          } else {
            // Single-Select: Check if current value exists in new options
            const isValueInNewOptions = newOptions.some(
              opt => opt.value === currentValue
            );

            if (!isValueInNewOptions && currentValue) {
              // Keep the current value as an option if it doesn't exist in new options
              const currentLabel =
                getValues(`${metadata.id}_label`) || currentValue.toString();
              setOptions([
                ...newOptions,
                { value: currentValue, label: currentLabel }
              ]);
              console.log(
                `✋ Modal Dropdown: Keeping existing value ${currentValue} for ${metadata.id}`
              );
            } else {
              setOptions(newOptions);
            }
          }

          optionsLoaded.current = true;
        } else {
          setOptions([]);
          optionsLoaded.current = true;
        }
      } catch (error) {
        console.error(
          `❌ Modal Dropdown: Error fetching options for ${metadata.id}:`,
          error
        );
        if (!field.value) {
          setOptions([]);
        }
        optionsLoaded.current = true;
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [
    metadata.dependsOn,
    watch(metadata.dependsOn),
    metadata.endpoint,
    metadata.id,
    metadata.multiSelect,
    setValue,
    field.value,
    externalOptions
  ]);

  // Get option label by value
  const getOptionLabel = value => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value.toString();
  };

  useEffect(() => {
    if (setDropdownOptions) {
      setDropdownOptions(metadata.id, options);
    }
  }, [options, metadata.id, setDropdownOptions]);

  // Filter options to exclude already selected values
  const filteredOptions = options.filter(
    option => !excludeValues.includes(option.value)
  );

  // Watch field value for auto-population of dependents
  useEffect(() => {
    if (!field.value || options.length === 0) return;

    const selectedOption = options.find(
      opt => String(opt.value) === String(field.value)
    );

    // Find all fields that depend on this field and have populate_from_row_keys
    // Check both explicit metadata.dependents and reverse lookup via dependsOn
    const explicitDeps = (metadata.dependents || [])
      .map(depId =>
        Array.isArray(allFields)
          ? allFields.find(f => f.id === depId)
          : allFields[depId]
      )
      .filter(Boolean);

    const reverseDeps = Object.values(allFields).filter(
      f =>
        f.dependsOn &&
        (Array.isArray(f.dependsOn)
          ? f.dependsOn.includes(metadata.id)
          : f.dependsOn === metadata.id)
    );

    // Combine and deduplicate
    const combinedDeps = [...explicitDeps];
    reverseDeps.forEach(rd => {
      if (!combinedDeps.find(cd => cd.id === rd.id)) {
        combinedDeps.push(rd);
      }
    });

    const dependentsToProcess = combinedDeps.filter(
      f => f.populate_from_row_keys
    );

    if (selectedOption && dependentsToProcess.length > 0) {
      console.log(
        `🎯 Modal Dropdown: Auto-populating ${dependentsToProcess.length} dependents for ${metadata.id}`
      );
      console.log('Selected option data:', selectedOption);

      dependentsToProcess.forEach(depMetadata => {
        const depId = depMetadata.id;
        if (depMetadata.populate_from_row_keys) {
          depMetadata.populate_from_row_keys.forEach(key => {
            const valToPopulate = selectedOption[key];
            const currentValue = getValues(depId);

            if (
              valToPopulate !== undefined &&
              valToPopulate !== null &&
              String(valToPopulate) !== String(currentValue)
            ) {
              console.log(
                `✨ Auto-populating ${depId} from ${metadata.id} [${key}] = ${valToPopulate}`
              );

              // Use a small timeout to ensure setValue works after any other state updates
              setTimeout(() => {
                setValue(depId, valToPopulate);

                // If it's a dropdown, also set the label if available in the option
                const labelKey = `${key}_label`;
                if (
                  depMetadata.type === 'dropdown' &&
                  selectedOption[labelKey] &&
                  String(selectedOption[labelKey]) !==
                    String(getValues(`${depId}_label`))
                ) {
                  console.log(
                    `🏷️ Setting label for ${depId}: ${selectedOption[labelKey]}`
                  );
                  setValue(`${depId}_label`, selectedOption[labelKey]);
                }
              }, 50);
            }
          });
        }
      });
    }
  }, [
    field.value,
    options,
    metadata.id,
    metadata.dependents,
    allFields,
    setValue,
    getValues
  ]);

  // Handle dropdown change
  const handleChange = e => {
    const value = e.target.value;
    field.onChange(value);

    // Store the label for display purposes
    if (value) {
      const selectedOption = options.find(
        opt => String(opt.value) === String(value)
      );
      if (selectedOption) {
        // Store the label separately for display in tables
        setValue(`${metadata.id}_label`, selectedOption.label);
      }
    } else {
      setValue(`${metadata.id}_label`, '');
    }

    // If there are dependent fields, reset them
    if (metadata.resetDependents) {
      const formValues = watch();
      Object.keys(formValues).forEach(key => {
        const field = metadata.resetDependents.find(id => id === key);
        if (field) {
          setValue(field, metadata.multiSelect ? [] : '');
          setValue(`${field}_label`, '');
        }
      });
    }
  };

  // Handle multi-select dropdown change
  const handleMultiSelectChange = e => {
    // Get all selected options
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      option => option.value
    );
    field.onChange(selectedOptions);

    // Store labels for each selected value
    selectedOptions.forEach(value => {
      const option = options.find(opt => opt.value === value);
      if (option) {
        setValue(`${metadata.id}_label_${value}`, option.label);
      }
    });
  };

  return (
    <Form.Group as={Col} sm={12} md={12} className="mb-3">
      <Form.Label>
        {metadata.label}
        {metadata?.validation?.required && (
          <span className="text-danger ms-1">*</span>
        )}
        {metadata?.info_key && (
          <div
            style={{
              marginLeft: '5px',
              cursor: 'pointer',
              position: 'relative',
              display: 'inline-block'
            }}
            title={metadata.info_key}
          >
            <BsInfoCircleFill size={18} />
          </div>
        )}
      </Form.Label>

      {metadata.multiSelect ? (
        <Form.Select
          multiple
          value={Array.isArray(field.value) ? field.value : []}
          disabled={isDisabled || isLoading || metadata.isEditable === false}
          isInvalid={!!errors[metadata.id]}
          onChange={handleMultiSelectChange}
          style={{ height: '120px' }}
        >
          {/* {options.map(option => ( */}
          {filteredOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      ) : (
        <Form.Select
          {...field}
          defaultValue={metadata.defaultValue}
          disabled={isDisabled || isLoading || metadata.isEditable === false}
          isInvalid={!!errors[metadata.id]}
          onChange={handleChange}
        >
          <option value="">
            {isLoading
              ? 'Loading options...'
              : metadata.placeholder || 'Select an option'}
          </option>

          {/* {options.map(option => ( */}
          {filteredOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )}

      {/* Show loading indicator for external options */}
      {/* {externalOptions === null && metadata.dependsOn && (
        <div className="text-muted mt-1 small">
          {options.length === 0 ? 'Waiting for parent selection...' : ''}
        </div>
      )} */}
      {externalOptions === null &&
        metadata.dependsOn &&
        getValues(metadata.dependsOn) !== undefined && ( // <-- Ensure dependent field exists
          <div className="text-muted mt-1 small">
            {options.length === 0 && !watch(metadata.dependsOn)
              ? 'Waiting for parent selection...'
              : ''}
          </div>
        )}

      {errors[metadata.id] && (
        <Form.Control.Feedback type="invalid">
          {errors[metadata.id]?.message}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default DynamicDropdown;
